import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field, useFormikContext, FormikHelpers } from "formik";
import * as Yup from "yup";
import { Typography, Alert, Paper, Modal } from "@mui/material";
import { Tooltip } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { ReactComponent as DeleteIcon } from "../../assets/images/delete-bin-line.svg";
import { ReactComponent as EditIcon } from "../../assets/images/edit-2-line.svg";
import { ReactComponent as EyeIcon } from "../../assets/images/eye-line.svg";
import { ReactComponent as InformationIcon } from "../../assets/images/information-line.svg";
import AddButton from "../../components/ui/AddButton";
import Breadcrumb from "../../components/common/Breadcrumb";
import NavigationTags from "../../components/common/NavigationTags";
import { generateHubFolderCategoriesBreadcrumbs } from "../../utils/breadcrumbHelpers";
import { TextField, NumberField } from "../../components/common/FormFields";
import FormGroup from "../../components/common/FormGroup";
import { getCategories, createCategory, updateCategory, deleteCategory } from "../../api/categories";
import { getSingleTrackFirstFilters, getSingleTrackFolders } from "../../api/company";
import ButtonComponent from "../../components/ui/Button";
import { useNavigate } from "react-router-dom";
import { CompanySingleTrackFolder, GetCategoriesResponse } from "../../types/requestsOutputs";
import DeleteModal from "../../components/modals/DeleteModal";
import { useGlobalState } from "../../context/useGlobalState";
import { useNotification } from "../../context/NotificationContext";
import ToggleSwitch from "../../components/common/ToggleSwitch";
import hubStyles from "./Hub.module.css";
import pageHeader from "../../components/layouts/PageHeader.module.css";
import studio from "../../components/modals/StudioModal.module.css";
import stepStyles from "../track/create/createStep.module.css";

interface CategoryFormValues {
    categoryName: string;
    position?: number;
}

type Category = GetCategoriesResponse[0];

const CategoryForm = ({
    onClose,
    isEdit = false,
    initialValues,
    originalCategory,
    hub,
}: {
    onClose: () => void;
    isEdit?: boolean;
    initialValues?: CategoryFormValues;
    originalCategory?: Category | null;
    hub: CompanySingleTrackFolder | null;
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { errors, touched, isSubmitting, submitForm } = useFormikContext<CategoryFormValues>();
    const { data, loading } = useGlobalState();

    const language = data.languagesAI.find((lang) => lang.value === hub?.originLanguageId)?.label;

    if (loading) {
        return (
            <div className="form-loading-container">
                <div className={hubStyles.formLoadingSpinner}></div>
                <p className="form-loading-text">{t("loadingLanguages")}</p>
            </div>
        );
    }

    return (
        <Form>
            <div className="category-form-container">
                <h2 className={studio.dialogTitle}>{isEdit ? t("editCategory") : t("addNewCategory")}</h2>

                {/* Category Name */}
                <FormGroup
                    title={t("categoryName")}
                    subtitle={t("nameWithOriginLanguage", { language: language })}
                    className={`${stepStyles.stepDescription} form-group-spacing`}
                    required={!isEdit}
                >
                    <Field name="categoryName" component={TextField} placeholder={t("enterCategoryName")} />
                    {touched.categoryName && errors.categoryName && (
                        <Alert severity="error" className="form-error-alert">
                            {errors.categoryName}
                        </Alert>
                    )}
                </FormGroup>

                {/* Position */}
                <FormGroup
                    title={t("position")}
                    subtitle={t("positionDescription")}
                    className={`${stepStyles.stepDescription} form-group-spacing`}
                >
                    <Field name="position" component={NumberField} placeholder={t("enterPosition")} min={0} />
                </FormGroup>

                {/* Action Buttons */}
                <div className={studio.dialogActions}>
                    <ButtonComponent
                        variant="cancel"
                        shape="pill"
                        label={t("cancel")}
                        onClick={onClose}
                        disabled={isSubmitting}
                    />
                    <Tooltip
                        title={
                            Object.keys(errors).length > 0
                                ? t("formIncompleteTooltip", { fields: Object.values(errors).join(", ") })
                                : ""
                        }
                        arrow
                    >
                        <span>
                            <ButtonComponent
                                variant="primary"
                                shape="pill"
                                label={isEdit ? t("updateCategory") : t("addCategory")}
                                onClick={submitForm}
                                disabled={isSubmitting || Object.keys(errors).length > 0}
                            />
                        </span>
                    </Tooltip>
                </div>
            </div>
        </Form>
    );
};

const HubFolderCategories: React.FC = () => {
    const { t: rawT, i18n } = useTranslation();
    const t = rawT as any;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const folderId = searchParams.get("folderId");
    const hubId = searchParams.get("hubId");

    const [categories, setCategories] = useState<GetCategoriesResponse>([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState<Category | null>(null);
    const [selectedCategoryToDelete, setSelectedCategoryToDelete] = useState<Category | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [folderName, setFolderName] = useState<string>("");
    const [hub, setHub] = useState<CompanySingleTrackFolder | null>(null);
    const { loading: loadingLanguages } = useGlobalState();

    const { showNotification } = useNotification();

    // Dynamic Yup validation schema
    const getValidationSchema = () => {
        return Yup.object({
            categoryName: Yup.string().required(t("categoryNameRequired")),
            position: Yup.number().integer(t("positionMustBeInteger")).min(0, t("positionMustBePositive")),
        });
    };

    // Initial values for create
    const initialValues: CategoryFormValues = {
        categoryName: "",
        position: 0,
    };

    // Fetch categories and names on component mount
    useEffect(() => {
        fetchCategories();
        fetchNames();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchNames = async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token || !hubId || !folderId) return;

            // Fetch hub name
            const hubsResponse = await getSingleTrackFolders({
                token,
                data: { language: i18n.language || "en" },
            });
            const currentHub = hubsResponse.find((h) => h.id === hubId);
            if (currentHub) {
                setHub(currentHub);
            }

            // Fetch folder name
            const foldersResponse = await getSingleTrackFirstFilters({
                token,
                data: {
                    companySingleTrackFolderId: hubId,
                    language: i18n.language || "en",
                },
            });
            const currentFolder = foldersResponse.find((f) => f.id === folderId);
            if (currentFolder) {
                setFolderName(currentFolder.internalName);
            }
        } catch (error) {
            console.error("Error fetching names:", error);
        }
    };

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token || !folderId) {
                console.error("Token not found");
                return;
            }
            const response = await getCategories({
                token,
                data: {
                    firstFilterId: folderId,
                },
            });
            setCategories(response);
        } catch (error) {
            console.error("Failed to fetch categories", error);
            showNotification({
                message: t("errorFetchingCategories"),
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCreateCategory = async (
        values: CategoryFormValues,
        formikHelpers: FormikHelpers<CategoryFormValues>,
    ) => {
        try {
            const token = localStorage.getItem("token");
            if (!token || !folderId) {
                showNotification({
                    message: t("noTokenFound"),
                    type: "error",
                });
                return;
            }

            if (editingCategory) {
                // Update existing category - build update data with only changed fields
                const updatePayload: any = { categoryId: editingCategory.id };

                // Only include fields that have changed
                if (values.categoryName !== editingCategory.name) {
                    updatePayload.categoryName = values.categoryName;
                }
                if (values.position !== editingCategory.position) {
                    updatePayload.position = values.position;
                }

                await updateCategory({
                    token,
                    payload: updatePayload,
                });
                showNotification({
                    message: t("categoryUpdatedSuccessfully"),
                    type: "success",
                });
            } else {
                // Create new category
                await createCategory({
                    token,
                    payload: {
                        categoryName: values.categoryName,
                        firstFilterId: folderId,
                        position: values.position,
                    },
                });
                showNotification({
                    message: t("categoryCreatedSuccessfully"),
                    type: "success",
                });
            }

            setModalOpen(false);
            setEditingCategory(null);
            fetchCategories();
            formikHelpers.resetForm();
        } catch (error) {
            console.error("Failed to save category", error);
            showNotification({
                message: editingCategory ? t("errorUpdatingCategory") : t("errorCreatingCategory"),
                type: "error",
            });
        }
    };

    const handleDeleteCategory = async (categoryId: string) => {
        const token = localStorage.getItem("token");
        if (!token) {
            showNotification({
                message: t("noTokenFound"),
                type: "error",
            });
            return;
        }

        // Store the category being deleted for potential revert
        const categoryToDelete = categories.find((c) => c.id === categoryId);
        if (!categoryToDelete) return;

        // Optimistic update: immediately remove from UI
        setCategories((prevCategories) => prevCategories.filter((c) => c.id !== categoryId));

        try {
            await deleteCategory({ token, categoryId });
            showNotification({
                message: t("categoryDeletedSuccessfully"),
                type: "success",
            });
        } catch (error) {
            console.error("Failed to delete category", error);
            showNotification({
                message: t("errorDeletingCategory"),
                type: "error",
            });

            // Revert the optimistic update on error by adding the category back
            setCategories((prevCategories) => [...prevCategories, categoryToDelete]);
        }
    };

    const openCreateModal = () => {
        setEditingCategory(null);
        setModalOpen(true);
    };

    const handleEditCategory = (category: Category) => {
        setEditingCategory(category);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingCategory(null);
    };

    const handleCreateSingleTrack = (categoryId: string) => {
        navigate(`/hubs/folders/categories/tracks?folderId=${folderId}&categoryId=${categoryId}&hubId=${hubId}`);
    };

    const handleViewCategory = (categoryId: string) => {
        const hubToken = hub?.oTPToken?.[0]?.token;
        if (!hubToken || !folderId) return;
        const url = `https://hub.rewindstories.com/${hubId}/${folderId}?cat=${categoryId}`;
        window.open(url, "_blank");
    };

    const handleToggleStatus = async (category: Category) => {
        const token = localStorage.getItem("token");
        if (!token || !folderId) {
            showNotification({
                message: t("noTokenFound"),
                type: "error",
            });
            return;
        }

        // Optimistic update: immediately update the UI
        const newStatus = !category.status;
        setCategories((prevCategories) =>
            prevCategories.map((c) => (c.id === category.id ? { ...c, status: newStatus } : c)),
        );

        try {
            await updateCategory({
                token,
                payload: {
                    categoryId: category.id,
                    status: newStatus,
                },
            });
            // No need to refetch since we already updated optimistically
        } catch (error) {
            console.error("Error updating category status:", error);
            showNotification({
                message: t("errorUpdatingCategoryStatus"),
                type: "error",
            });

            // Revert the optimistic update on error
            setCategories((prevCategories) =>
                prevCategories.map((c) => (c.id === category.id ? { ...c, status: category.status } : c)),
            );
        }
    };

    const sortedCategories = useMemo(() => {
        return [...categories].sort((a, b) => {
            const posA = a.position || 0;
            const posB = b.position || 0;

            // Case 1: both have non-zero positions
            if (posA !== 0 && posB !== 0) {
                if (posA !== posB) return posA - posB;
                return a.name.localeCompare(b.name);
            }

            // Case 2: one has non-zero, one zero
            if (posA !== 0 && posB === 0) return -1;
            if (posB !== 0 && posA === 0) return 1;

            // Case 3: both zero → alphabetical
            return a.name.localeCompare(b.name);
        });
    }, [categories]);

    return (
        <div className={`${hubStyles.root} ${hubStyles.page}`}>
            <div className={hubStyles.pageHeader}>
                {hub && (
                    <Breadcrumb
                        classes={{
                            nav: hubStyles.breadcrumbNav,
                            separator: hubStyles.breadcrumbSeparator,
                            link: hubStyles.breadcrumbLink,
                            text: hubStyles.breadcrumbText,
                            active: hubStyles.breadcrumbActive,
                        }}
                        items={generateHubFolderCategoriesBreadcrumbs({
                            hubId,
                            hubName: hub?.resolvedName || "",
                            folderId,
                            folderName,
                            t,
                        })}
                    />
                )}
                {hub && (
                    <div className={pageHeader.titleSection}>
                        <div className={pageHeader.content}>
                            <h2 className={hubStyles.settingsTitle}>{t("manageFolderContent")}</h2>
                            <div className={hubStyles.pageSubtitleWithInfo}>
                                <Typography variant="body2" className={hubStyles.pageSubtitle}>
                                    {t("manageCategoriesDescription")}
                                </Typography>
                                <Tooltip title={t("categoriesExplanationTooltip")} arrow placement="right">
                                    <InformationIcon className={hubStyles.infoIcon} />
                                </Tooltip>
                            </div>
                        </div>
                        <AddButton
                            labeledClassName={hubStyles.addButtonLabeled}
                            onClick={openCreateModal}
                            tooltip={t("addNewCategory")}
                            label={t("newCategory")}
                        />
                    </div>
                )}
                <NavigationTags
                    classes={{
                        container: hubStyles.navigationTags,
                        tag: hubStyles.navigationTag,
                        tagActive: hubStyles.navigationTagActive,
                        tagDisabled: hubStyles.navigationTagDisabled,
                        arrow: hubStyles.navigationTagArrow,
                    }}
                    tags={[
                        { label: t("hub"), path: "/hubs" },
                        { label: t("page"), path: `/hubs/folders?hubId=${hubId}` },
                        { label: t("category"), isActive: true },
                        { label: t("content") },
                    ]}
                />
            </div>

            {/* Categories List */}
            <div className="categories-section">
                {loading || loadingLanguages ? (
                    <div className="form-loading-container">
                        <div className={hubStyles.formLoadingSpinner}></div>
                        <p className="form-loading-text">{t("loadingCategories")}</p>
                    </div>
                ) : sortedCategories.length === 0 ? (
                    <div className={hubStyles.emptyState}>
                        <div className={hubStyles.emptyStateIcon} aria-hidden>
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <rect x="3" y="3" width="7" height="7" />
                                <rect x="14" y="3" width="7" height="7" />
                                <rect x="14" y="14" width="7" height="7" />
                                <rect x="3" y="14" width="7" height="7" />
                            </svg>
                        </div>
                        <Typography variant="h6" className={hubStyles.emptyStateTitle}>
                            {t("noCategoriesYet")}
                        </Typography>
                        <Typography variant="body2" className={hubStyles.emptyStateText}>
                            {t("noCategoriesDescription")}
                        </Typography>
                        <AddButton
                            labeledClassName={hubStyles.emptyStateButton}
                            onClick={openCreateModal}
                            tooltip={t("addNewCategory")}
                            label={t("newCategory")}
                        />
                    </div>
                ) : (
                    <div className={hubStyles.foldersGrid}>
                        {sortedCategories.map((category) => {
                            return (
                                <div
                                    key={category.id}
                                    className={hubStyles.folderCard}
                                    onClick={() => handleCreateSingleTrack(category.id)}
                                >
                                    {/* Category type icon */}
                                    <div className={hubStyles.rowTypeIcon}>
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <polygon points="12 2 2 7 12 12 22 7 12 2" />
                                            <polyline points="2 17 12 22 22 17" />
                                            <polyline points="2 12 12 17 22 12" />
                                        </svg>
                                    </div>

                                    {/* Info */}
                                    <div className={hubStyles.rowInfo}>
                                        <p className={hubStyles.rowName}>{category.name}</p>
                                        <p className={hubStyles.rowMeta}>
                                            {t("page")}: {folderName}
                                            {category.position ? ` · ${t("position")} ${category.position}` : ""}
                                        </p>
                                    </div>

                                    {/* Status toggle */}
                                    <div
                                        className={hubStyles.statusToggleContainer}
                                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                    >
                                        <span
                                            className={`${hubStyles.statusLabel}${category.status ? ` ${hubStyles.statusLabelActive}` : ""}`}
                                        >
                                            {category.status ? t("active") : t("inactive")}
                                        </span>
                                        <ToggleSwitch
                                            checked={category.status || false}
                                            onChange={() => handleToggleStatus(category)}
                                        />
                                    </div>

                                    {/* Separator */}
                                    <div className={hubStyles.rowSeparator} />

                                    {/* Actions: eye + pen + trash */}
                                    <div
                                        className={hubStyles.rowActions}
                                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                    >
                                        <Tooltip title={t("viewCategory")} arrow>
                                            <span>
                                                <EyeIcon
                                                    className={hubStyles.mutedIconButton}
                                                    style={{
                                                        opacity: hub?.oTPToken?.[0]?.token ? 1 : 0.35,
                                                        cursor: hub?.oTPToken?.[0]?.token ? "pointer" : "not-allowed",
                                                    }}
                                                    onClick={(e: React.MouseEvent) => {
                                                        e.stopPropagation();
                                                        if (hub?.oTPToken?.[0]?.token) handleViewCategory(category.id);
                                                    }}
                                                />
                                            </span>
                                        </Tooltip>
                                        <Tooltip title={t("edit")} arrow>
                                            <EditIcon
                                                className={hubStyles.hubSettingsIconButton}
                                                onClick={(e: React.MouseEvent) => {
                                                    e.stopPropagation();
                                                    handleEditCategory(category);
                                                }}
                                            />
                                        </Tooltip>
                                        <Tooltip title={t("delete")} arrow>
                                            <DeleteIcon
                                                className={hubStyles.hubDeleteIconButton}
                                                onClick={(e: React.MouseEvent) => {
                                                    e.stopPropagation();
                                                    setSelectedCategoryToDelete(category);
                                                    setIsDeleteModalOpen(true);
                                                }}
                                            />
                                        </Tooltip>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            {selectedCategoryToDelete && (
                <DeleteModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => {
                        setIsDeleteModalOpen(false);
                        setSelectedCategoryToDelete(null);
                    }}
                    onConfirm={() => {
                        handleDeleteCategory(selectedCategoryToDelete.id);
                        setSelectedCategoryToDelete(null);
                    }}
                    message={t("deleteCategoryConfirmation")}
                    title={t("deleteCategory")}
                />
            )}

            {/* Create/Edit Modal */}
            <Modal open={modalOpen} onClose={closeModal} className={studio.folderOverlay}>
                <Paper className={studio.folderContent}>
                    <Formik
                        initialValues={
                            editingCategory
                                ? {
                                      categoryName: editingCategory.name,
                                      position: editingCategory.position || 0,
                                  }
                                : initialValues
                        }
                        validationSchema={getValidationSchema()}
                        onSubmit={handleCreateCategory}
                        enableReinitialize
                        validateOnMount
                    >
                        <CategoryForm
                            onClose={closeModal}
                            isEdit={!!editingCategory}
                            originalCategory={editingCategory}
                            hub={hub}
                        />
                    </Formik>
                </Paper>
            </Modal>
        </div>
    );
};

export default HubFolderCategories;
